import React from "react";

/**
 * Minimal dependency-free SVG line chart.
 * data: [{ x: string|number, y: number|null }]
 */
export default function LineChart({
  data = [],
  height = 180,
  color = "#5C7A4F",
  unit = "",
  showAxis = true,
}) {
  const valid = data.filter((d) => d.y !== null && d.y !== undefined && !isNaN(d.y));
  if (valid.length === 0) {
    return (
      <div
        style={{ height }}
        className="flex items-center justify-center text-xs text-[var(--lc-muted)] bg-[var(--lc-cream-soft)] border border-dashed border-[var(--lc-line)] rounded-sm"
      >
        No data yet
      </div>
    );
  }
  const width = 600;
  const pad = { top: 14, right: 14, bottom: 24, left: 36 };
  const w = width - pad.left - pad.right;
  const h = height - pad.top - pad.bottom;
  const ys = valid.map((d) => d.y);
  const yMin = Math.min(...ys);
  const yMax = Math.max(...ys);
  const range = yMax - yMin || 1;
  const yPad = range * 0.15;
  const domMin = yMin - yPad;
  const domMax = yMax + yPad;
  const sx = (i) => (valid.length === 1 ? w / 2 : (i * w) / (valid.length - 1));
  const sy = (y) => h - ((y - domMin) / (domMax - domMin)) * h;

  const pts = valid.map((d, i) => `${sx(i).toFixed(1)},${sy(d.y).toFixed(1)}`).join(" ");
  const areaPath = `M ${sx(0).toFixed(1)},${h} L ${pts.split(" ").join(" L ")} L ${sx(valid.length - 1).toFixed(1)},${h} Z`;

  const latest = valid[valid.length - 1];
  const first = valid[0];
  const delta = latest.y - first.y;

  return (
    <div className="w-full">
      <div className="flex items-end justify-between mb-2">
        <div className="font-serif-display text-2xl leading-none">
          {latest.y}
          {unit && <span className="text-sm text-[var(--lc-muted)] ml-1">{unit}</span>}
        </div>
        {valid.length > 1 && (
          <div className={`text-xs font-semibold ${delta >= 0 ? "text-emerald-700" : "text-red-700"}`}>
            {delta >= 0 ? "▲" : "▼"} {Math.abs(delta).toFixed(1)} {unit} since start
          </div>
        )}
      </div>
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full" preserveAspectRatio="none" style={{ height }}>
        <g transform={`translate(${pad.left},${pad.top})`}>
          {showAxis && (
            <>
              <line x1={0} y1={h} x2={w} y2={h} stroke="var(--lc-line)" strokeWidth="1" />
              <line x1={0} y1={0} x2={0} y2={h} stroke="var(--lc-line)" strokeWidth="1" />
              <text x={-6} y={0} fontSize="10" fill="#9b9b9b" textAnchor="end" dominantBaseline="hanging">
                {domMax.toFixed(1)}
              </text>
              <text x={-6} y={h} fontSize="10" fill="#9b9b9b" textAnchor="end" dominantBaseline="auto">
                {domMin.toFixed(1)}
              </text>
            </>
          )}
          <path d={areaPath} fill={color} opacity="0.1" />
          <polyline points={pts} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          {valid.map((d, i) => (
            <circle key={i} cx={sx(i)} cy={sy(d.y)} r="2.5" fill={color} />
          ))}
          <text x={0} y={h + 14} fontSize="10" fill="#9b9b9b">
            {valid[0].x}
          </text>
          <text x={w} y={h + 14} fontSize="10" fill="#9b9b9b" textAnchor="end">
            {valid[valid.length - 1].x}
          </text>
        </g>
      </svg>
    </div>
  );
}
